import { compact } from 'lodash-es';
import { defineMessage, MessageDescriptor } from 'react-intl';

import { BusinessVerificationLocationType } from '../../../common/interfaces/entities';
import { VerifyWithProofPersonalDocumentType } from './types';

export const requiredFieldsMessages = ({
  isCompanyVerified,
}: {
  isCompanyVerified: boolean;
}): MessageDescriptor[] =>
  compact([
    !isCompanyVerified && {
      id: 'text-personal-npwp-and',
      defaultMessage:
        'To verify with Personal NPWP or Perseroan Perorangan NPWP, use the <proofOfOwnership>Proof of Ownership method</proofOfOwnership>',
    },
    {
      id: 'text-if-the-account-name',
      defaultMessage:
        'If your company account uses a brand name, upload a Social Media Manager Dashboard or HAKI (Hak Cipta) certificate to verify ownership.',
    },
  ]);

export const LegalDocumentSource = {
  DJP_WEBSITE: 'DJP_WEBSITE',
  CORETAX_WEBSITE: 'CORETAX_WEBSITE',
} as const;
export type LegalDocumentSource =
  (typeof LegalDocumentSource)[keyof typeof LegalDocumentSource];

export const LegalDocumentMetadataChecks = {
  [LegalDocumentSource.DJP_WEBSITE]: {
    creator: 'JasperReports',
    producer: 'iText',
  },
  [LegalDocumentSource.CORETAX_WEBSITE]: {
    creator: 'INFINICA',
    producer: 'INFINICA',
  },
};

export const LEGAL_DOCUMENT_ACCEPTED_FILE_TYPE = ['application/pdf'];

export const DefaultFileUploadHelperText = defineMessage({
  id: 'text-acceptable-formats-max',
  defaultMessage:
    '<bold>Acceptable formats:</bold> pdf, jpg, jpeg, png | <bold>Maximum size: {maxSize}MB</bold>',
});

export const proofOfOwnershipRequiredFieldsMessages: MessageDescriptor[] = [
  {
    id: 'text-whatsapp-verification',
    defaultMessage: 'WhatsApp Verification',
  },
  {
    id: 'text-personal-npwp-or-ktp',
    defaultMessage: 'Personal NPWP/KTP',
  },
  {
    id: 'text-personal-npwp-ktp-number',
    defaultMessage: 'Personal NPWP/KTP Number',
  },
  {
    id: 'text-personal-nib-optional',
    defaultMessage: 'Personal NIB (Optional)',
  },
  {
    id: 'text-offline-online-bussiness-activity-images',
    defaultMessage: 'Offline/Online Business Activity Images',
  },
];

export const personalDocumentTypeToText = {
  [VerifyWithProofPersonalDocumentType.NPWP]: {
    optionText: {
      id: 'text-personal-npwp',
      defaultMessage: 'Personal NPWP',
    },
    documentNumber: {
      title: {
        id: 'text-personal-npwp-number',
        defaultMessage: 'Personal NPWP Number',
      },
      placeholder: {
        id: 'text-placeholder-npwp-number',
        defaultMessage: 'Enter NPWP Number',
      },
    },
    tooltip: {
      title: {
        id: 'text-personal-npwp',
        defaultMessage: 'Personal NPWP',
      },
      description: {
        id: 'text-colored-newest-format-preferred',
        defaultMessage:
          'Colored, newest format preferred (e-NPWP) that has the QR code.',
      },
      images: [
        {
          src: 'images/tier-verification/personal-npwp-image.png',
        },
      ],
    },
  },
  [VerifyWithProofPersonalDocumentType.KTP]: {
    optionText: {
      id: 'text-personal-ktp',
      defaultMessage: 'Personal KTP',
    },
    documentNumber: {
      title: {
        id: 'text-personal-ktp-number',
        defaultMessage: 'Personal KTP Number',
      },
      placeholder: {
        id: 'text-placeholder-ktp-number',
        defaultMessage: 'Enter KTP Number',
      },
    },
    tooltip: {
      title: {
        id: 'text-personal-ktp',
        defaultMessage: 'Personal KTP',
      },
      description: {
        id: 'text-colored-with-full-id',
        defaultMessage: 'Colored with full ID in view',
      },
      images: [
        {
          src: 'images/tier-verification/personal-ktp-image.png',
        },
      ],
    },
  },
} as const;

export const companyNPWPTooltip = {
  title: {
    id: 'text-company-npwp',
    defaultMessage: 'Company NPWP',
  },
  description: {
    id: 'text-company-npwp-colored-newest-format',
    defaultMessage: 'Colored, newest format (e-NPWP) that has the QR code.',
  },
  description2: {
    id: 'text-company-npwp-preferred-original-pdf',
    defaultMessage: 'Preferred original PDF downloaded from DJP/Coretax',
  },
  images: [
    {
      src: 'images/tier-verification/company-npwp-image.png',
    },
  ],
} as const;

export const businessLocationTypeToText = {
  [BusinessVerificationLocationType.ONLINE]: {
    optionText: {
      id: 'text-online-business-activity',
      defaultMessage: 'Online Business Activity Images (Min {count})',
    },
    tooltip: {
      title: {
        id: 'text-online-business-activity-title',
        defaultMessage: 'Online Business Activity Images',
      },
      description: {
        id: 'text-online-business-activity-requirement',
        defaultMessage:
          'Attaching online signage of business operations (photos and/or videos). Example: Business Instagram Account, Shopee Seller Account, and others similar.',
      },
      images: [
        {
          src: 'images/tier-verification/online-business-activity-image-1.png',
          width: '182px',
        },
        {
          src: 'images/tier-verification/online-business-activity-image-2.png',
          width: '182px',
        },
      ],
      imagesDirection: 'row' as const,
    },
  },
  [BusinessVerificationLocationType.OFFLINE]: {
    optionText: {
      id: 'text-offline-business-activity',
      defaultMessage: 'Offline Business Activity Images (Min {count})',
    },
    tooltip: {
      title: {
        id: 'text-offline-business-activity-title',
        defaultMessage: 'Offline Business Activity Images',
      },
      description: {
        id: 'text-offline-business-activity-requirement',
        defaultMessage:
          'Attaching offline business activity operations (photos and/or videos). Example: Business Buildings, Store Operations, and others similar.',
      },
      images: [
        {
          src: 'images/tier-verification/offline-business-activity-image-1.png',
        },
        {
          src: 'images/tier-verification/offline-business-activity-image-2.png',
        },
      ],
      imagesDirection: 'column' as const,
    },
  },
};

export const MIN_BUSINESS_ACTIVITY_IMAGES = 1;
